import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Field, NeedClinic } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, useLoad } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/constants";
import { allowedCategories } from "./Services";

const blankItem = { description: "", category: "consultation", qty: 1, unit_price: 0, service_id: "" };

function ItemRow({ it, i, cats, update, remove }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-end" data-testid={`invoice-item-row-${i}`}>
      <Field label="Description" className="col-span-12 sm:col-span-5"><Input value={it.description} onChange={(e) => update(i, { description: e.target.value })} data-testid={`invoice-item-desc-${i}`} /></Field>
      <Field label="Category" className="col-span-6 sm:col-span-3"><Select value={it.category} onValueChange={(v) => update(i, { category: v })}>
        <SelectTrigger data-testid={`invoice-item-category-${i}`}><SelectValue /></SelectTrigger>
        <SelectContent>{cats.map((k) => <SelectItem key={k} value={k}>{CATEGORY_LABELS[k]}</SelectItem>)}</SelectContent></Select></Field>
      <Field label="Qty" className="col-span-2 sm:col-span-1"><Input type="number" min="1" value={it.qty} onChange={(e) => update(i, { qty: e.target.value })} data-testid={`invoice-item-qty-${i}`} /></Field>
      <Field label="Unit price" className="col-span-3 sm:col-span-2"><Input type="number" min="0" value={it.unit_price} onChange={(e) => update(i, { unit_price: e.target.value })} data-testid={`invoice-item-price-${i}`} /></Field>
      <Button variant="ghost" size="icon" className="col-span-1" onClick={() => remove(i)} data-testid={`invoice-item-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
    </div>
  );
}

export default function NewInvoice() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const params = { clinic_id: activeClinicId };
  const [patients] = useLoad(activeClinicId ? "/patients" : null, params);
  const [services] = useLoad(activeClinicId ? "/services" : null, params);
  const [patientId, setPatientId] = useState(sp.get("patient") || "");
  const [items, setItems] = useState([{ ...blankItem }]);
  const [discount, setDiscount] = useState(0);
  const [ins, setIns] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const cur = activeClinic.currency;
  const cats = allowedCategories(activeClinic);
  const svc = (services || []).filter((s) => cats.includes(s.category));
  const update = (i, patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  const addService = (id) => {
    const s = svc.find((x) => x.id === id);
    if (s) setItems([...items.filter((x) => x.description), { description: s.name, category: s.category, qty: 1, unit_price: s.price, service_id: s.id }]);
  };
  const subtotal = items.reduce((a, x) => a + Number(x.qty || 0) * Number(x.unit_price || 0), 0);
  const total = Math.max(subtotal - Number(discount || 0), 0);
  const toggleIns = (on) => {
    const p = (patients || []).find((x) => x.id === patientId);
    setIns(on ? { provider: p?.insurance_provider || "", policy_no: p?.insurance_number || "", covered_amount: 0 } : null);
  };
  const submit = async () => {
    if (!patientId) return toast.error("Select a patient");
    setBusy(true);
    try {
      const body = { patient_id: patientId, discount: Number(discount || 0), items: items.map((x) => ({ ...x, qty: Number(x.qty), unit_price: Number(x.unit_price) })),
        insurance: ins ? { ...ins, covered_amount: Number(ins.covered_amount || 0) } : null };
      const { data } = await api.post("/invoices", body, { params });
      toast.success(`Invoice ${data.invoice_no} generated`);
      nav(`/invoices/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Generate invoice" subtitle={`All amounts in ${cur}. Payment is recorded afterwards by the accountant.`} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Patient">
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger data-testid="invoice-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>{(patients || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.patient_no} · {p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </Panel>
          <Panel title="Line items" action={svc.length ? (
            <Select value="" onValueChange={addService}><SelectTrigger className="w-52 rounded-full" data-testid="invoice-add-service-select"><SelectValue placeholder="Add from catalogue" /></SelectTrigger>
              <SelectContent>{svc.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {money(s.price, cur)}</SelectItem>)}</SelectContent></Select>) : null}>
            <div className="space-y-4">
              {items.map((it, i) => <ItemRow key={i} it={it} i={i} cats={cats} update={update} remove={remove} />)}
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems([...items, { ...blankItem }])} data-testid="invoice-add-item-button"><Plus className="mr-1 h-4 w-4" />Add line</Button>
            </div>
          </Panel>
          {activeClinic.modules?.insurance && (
            <Panel title="Insurance cover" action={<Switch checked={!!ins} onCheckedChange={toggleIns} data-testid="invoice-insurance-toggle" />}>
              {!ins ? <p className="text-sm text-slate-500">Toggle on if an insurer covers part of this bill.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Provider"><Input value={ins.provider} onChange={(e) => setIns({ ...ins, provider: e.target.value })} data-testid="invoice-insurance-provider-input" /></Field>
                  <Field label="Policy / member no."><Input value={ins.policy_no} onChange={(e) => setIns({ ...ins, policy_no: e.target.value })} data-testid="invoice-insurance-policy-input" /></Field>
                  <Field label={`Covered amount (${cur})`}><Input type="number" min="0" value={ins.covered_amount} onChange={(e) => setIns({ ...ins, covered_amount: e.target.value })} data-testid="invoice-insurance-covered-input" /></Field>
                </div>)}
            </Panel>)}
        </div>
        <Panel title="Summary" className="h-fit lg:sticky lg:top-4" testId="invoice-summary">
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd data-testid="invoice-subtotal">{money(subtotal, cur)}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Discount</dt><dd><Input type="number" min="0" className="h-8 w-28 text-right" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="invoice-discount-input" /></dd></div>
            <div className="flex justify-between border-t pt-3 text-base font-semibold"><dt>Total</dt><dd data-testid="invoice-total">{money(total, cur)}</dd></div>
            {ins && <div className="flex justify-between text-indigo-600"><dt className="flex items-center gap-1"><ShieldCheck className="h-4 w-4" />Insurer</dt><dd>{money(Math.min(Number(ins.covered_amount || 0), total), cur)}</dd></div>}
            {ins && <div className="flex justify-between font-semibold"><dt>Patient pays</dt><dd>{money(total - Math.min(Number(ins.covered_amount || 0), total), cur)}</dd></div>}
          </dl>
          <Button className="mt-6 w-full rounded-full" disabled={busy} onClick={submit} data-testid="invoice-submit-button">{busy ? "Generating…" : "Generate invoice"}</Button>
        </Panel>
      </div>
    </>
  );
}
